import React, { useState } from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { Download, AlertCircle, Loader2 } from 'lucide-react';
import { useCompanyBranding } from '@/hooks/useCompanyBranding';
import { ScenarioPDFDocument } from './ScenarioPDFDocument';
import { Button } from '@/components/ui/Button';
import type { ScenarioWithImprovements } from '@/types/work-scenarios';

interface ScenarioPDFDownloadButtonProps {
  scenario: ScenarioWithImprovements;
  dpeData: any;
  variant?: 'primary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onDownloadStart?: () => void;
  onDownloadComplete?: () => void;
  onDownloadError?: (error: Error) => void;
}

export const ScenarioPDFDownloadButton: React.FC<ScenarioPDFDownloadButtonProps> = ({
  scenario,
  dpeData,
  variant = 'primary',
  size = 'md',
  className = '',
  onDownloadStart,
  onDownloadComplete,
  onDownloadError,
}) => {
  const { companyName, companyLogoUrl, primaryColor, isProUser, loading: brandingLoading } = useCompanyBranding();
  const [error, setError] = useState<string | null>(null);

  const generateFileName = () => {
    const address = dpeData?.adresse_ban || 'scenario';
    const cleanAddress = address
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 50);

    const scenarioName = scenario.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 30);

    const date = new Date().toISOString().split('T')[0];
    return `scenario-travaux-${cleanAddress}-${scenarioName}-${date}.pdf`;
  };

  const DPEWIZARD_LOGO_URL = 'https://gitjbvjcespvbktehxcn.supabase.co/storage/v1/object/public/company-logos/default/dpewizard-logo.png';
  const effectiveCompanyName = isProUser ? companyName : 'DPEWizard';
  const effectiveLogoUrl = isProUser ? companyLogoUrl : DPEWIZARD_LOGO_URL;
  const effectivePrimaryColor = isProUser ? primaryColor : undefined;

  if (brandingLoading) {
    return (
      <Button variant={variant} size={size} disabled className={className}>
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Chargement...
      </Button>
    );
  }

  return (
    <>
      {error && (
        <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs text-red-900">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-xs font-medium text-red-600 hover:text-red-700 underline mt-1"
            >
              Réessayer
            </button>
          </div>
        </div>
      )}

      <PDFDownloadLink
        document={
          <ScenarioPDFDocument
            companyName={effectiveCompanyName}
            logoUrl={effectiveLogoUrl}
            primaryColor={effectivePrimaryColor}
            scenario={scenario}
            dpeData={dpeData}
          />
        }
        fileName={generateFileName()}
        className="inline-block"
      >
        {({ blob, url, loading: pdfLoading, error: pdfError }) => {
          if (pdfError) {
            if (!error) {
              setError('Erreur lors de la génération du PDF. Veuillez réessayer.');
              onDownloadError?.(pdfError);
            }
          }

          if (pdfLoading) {
            onDownloadStart?.();
          } else if (blob && url && !pdfError) {
            onDownloadComplete?.();
          }

          return (
            <Button
              variant={variant}
              size={size}
              className={className}
              disabled={pdfLoading}
            >
              {pdfLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Génération du PDF...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Télécharger le PDF
                </>
              )}
            </Button>
          );
        }}
      </PDFDownloadLink>
    </>
  );
};
